import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  ButtonGroup,
  Spinner,
  Banner,
  BlockStack,
  InlineStack,
  InlineGrid,
  Box,
  Divider,
  Icon,
  Badge,
} from "@shopify/polaris";
import {
  PlayIcon,
  TargetIcon,
  GiftCardIcon,
  AppsIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  AlertCircleIcon,
  PlusIcon,
  OrderIcon,
  ReceiptDollarIcon,
} from "@shopify/polaris-icons";
import { Link, useNavigate } from "react-router-dom";
import {
  getCampaigns,
  getGames,
  getRewardPolicies,
  updateCampaign,
  type Campaign,
  type CampaignVariant,
  type Game,
  type RewardPolicy,
} from "../api/campaigns";
import { WheelGamePreview } from "../components/games/previews/WheelGamePreview";
import {
  ScratchGamePreview,
  resolveScratchPreviewTheme,
} from "../components/games/previews/ScratchGamePreview";
import {
  getAnalyticsOverview,
  getAnalyticsTimeSeries,
  getCampaignAttributionSnapshot,
  type OverviewMetrics,
  type CampaignAttributionSnapshotRow,
} from "../api/analytics";
import { isShopInstallRedirectError } from "../api/admin-api-client";
import { StorefrontSetupGuidance } from "../components/StorefrontSetupGuidance/StorefrontSetupGuidance";

interface DashboardStats {
  activeCampaigns: number;
  totalCampaigns: number;
  totalGames: number;
  totalPolicies: number;
}

type OpportunityCard = {
  id: string;
  title: string;
  body: string;
  cta: string;
  path: string;
};

type AttentionItem = {
  id: string;
  label: string;
  detail: string;
  cta: string;
  path: string;
};

/**
 * Operational issues only (not growth). Dedupes against buildOpportunities so we
 * do not repeat the same “open draft” nudge when that card is already shown.
 */
function buildAttentionItems(campaigns: Campaign[], stats: DashboardStats): AttentionItem[] {
  const opportunities = buildOpportunities(campaigns, stats);
  const oppIds = new Set(opportunities.map((o) => o.id));
  const hasActive = stats.activeCampaigns > 0;
  const draftCampaigns = campaigns.filter((c) => c.status === "DRAFT");
  const singleDraftNoActive = !hasActive && draftCampaigns.length === 1;
  const skipDuplicateDraftNudge = singleDraftNoActive && oppIds.has("activate-draft");

  const paused = campaigns.filter((c) => c.status === "PAUSED");
  const campaignsWithoutVariants = campaigns.filter((c) => (c.variants?.length ?? 0) === 0);
  const draftsAllVariantsDisabled = campaigns.filter((c) => {
    if (c.status !== "DRAFT") return false;
    const v = c.variants ?? [];
    if (v.length === 0) return false;
    return v.every((x) => x.status === "DISABLED");
  });

  const items: AttentionItem[] = [];

  if (paused.length > 0) {
    const n = paused.length;
    items.push({
      id: "paused",
      label: n === 1 ? "One campaign is paused" : `${n} campaigns are paused`,
      detail: "Paused campaigns do not appear on your storefront.",
      cta: "Review campaigns",
      path: "/campaigns",
    });
  }

  if (items.length < 3 && campaignsWithoutVariants.length > 0) {
    const onlyIssueIsSingleDraft =
      skipDuplicateDraftNudge &&
      campaignsWithoutVariants.length === 1 &&
      campaignsWithoutVariants[0].status === "DRAFT";
    if (!onlyIssueIsSingleDraft) {
      const n = campaignsWithoutVariants.length;
      const first = campaignsWithoutVariants[0];
      items.push({
        id: "no-variants",
        label: n === 1 ? "One campaign has no game set up" : `${n} campaigns have no game set up`,
        detail: "Add a game and rewards so customers can play.",
        cta: n === 1 ? "Finish setup" : "View campaigns",
        path: n === 1 ? `/campaigns/${first.id}` : "/campaigns",
      });
    }
  }

  if (items.length < 3 && draftsAllVariantsDisabled.length > 0) {
    const onlyIssueIsSingleDraft =
      skipDuplicateDraftNudge && draftsAllVariantsDisabled.length === 1;
    if (!onlyIssueIsSingleDraft) {
      const n = draftsAllVariantsDisabled.length;
      const first = draftsAllVariantsDisabled[0];
      items.push({
        id: "draft-all-disabled",
        label:
          n === 1 ? "One draft has no game turned on" : `${n} drafts have no game turned on`,
        detail: "Enable at least one game variant before you can go live.",
        cta: "Continue setup",
        path: n === 1 ? `/campaigns/${first.id}` : "/campaigns",
      });
    }
  }

  return items.slice(0, 3);
}

function buildOpportunities(campaigns: Campaign[], stats: DashboardStats): OpportunityCard[] {
  const items: OpportunityCard[] = [];
  const hasActive = stats.activeCampaigns > 0;
  const firstDraft = campaigns.find((c) => c.status === "DRAFT");

  if (stats.totalPolicies === 0) {
    items.push({
      id: "reward-policies",
      title: "Create reward policies",
      body: "Define what customers can win before campaigns can issue rewards.",
      cta: "Configure rewards",
      path: "/rewards",
    });
  }

  if (stats.totalCampaigns === 0) {
    items.push({
      id: "first-campaign",
      title: "Create your first campaign",
      body: "Connect a game and rewards to a placement on your storefront.",
      cta: "Create campaign",
      path: "/campaigns/new",
    });
  } else if (!hasActive && firstDraft) {
    items.push({
      id: "activate-draft",
      title: "Activate a draft campaign",
      body: "Turn on a campaign so customers can see it on your storefront.",
      cta: "Open draft",
      path: `/campaigns/${firstDraft.id}`,
    });
  } else if (!hasActive && stats.totalCampaigns > 0) {
    items.push({
      id: "go-live",
      title: "Go live with a campaign",
      body: "Set at least one campaign to Active when it’s ready.",
      cta: "View campaigns",
      path: "/campaigns",
    });
  }

  if (items.length < 3 && stats.totalGames > 0) {
    items.push({
      id: "discount-wheel",
      title: "Add a discount wheel",
      body: "Use a wheel with instant discount or lead capture templates.",
      cta: "Browse games",
      path: "/games",
    });
  }

  if (items.length < 3 && stats.totalPolicies > 0) {
    items.push({
      id: "improve-rewards",
      title: "Improve your rewards",
      body: "Refine outcomes and limits in your reward policies.",
      cta: "Manage rewards",
      path: "/rewards",
    });
  }

  return items.slice(0, 3);
}

const PLACEMENT_LABELS: Record<string, string> = {
  cart: "Cart page",
  cart_drawer: "Cart drawer",
  product: "Product page",
  checkout: "Checkout",
  thankyou: "Thank you page",
  homepage: "Homepage",
};

function formatPlacement(placement: string): string {
  return PLACEMENT_LABELS[placement] ?? placement;
}

/** Dashboard card line: e.g. "Modal • after 3s" or "Inline • immediate". */
function formatDisplayTriggerSummary(campaign: Campaign): string {
  const mode = (campaign.displayMode ?? "modal").toLowerCase();
  const modeLabel = mode === "inline" ? "Inline" : "Modal";
  const trigger = (campaign.triggerType ?? "immediate").toLowerCase();
  if (trigger === "delay") {
    const ms = campaign.triggerDelayMs ?? 3000;
    const sec = Math.max(1, Math.round(ms / 1000));
    return `${modeLabel} • after ${sec}s`;
  }
  return `${modeLabel} • immediate`;
}

function campaignStatusBadge(status: Campaign["status"]) {
  switch (status) {
    case "ACTIVE":
      return <Badge tone="success">Active</Badge>;
    case "PAUSED":
      return <Badge tone="warning">Paused</Badge>;
    case "DRAFT":
      return <Badge>Draft</Badge>;
    case "ENDED":
      return <Badge tone="info">Ended</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

/** v1 insight from session count only (list API exposes `_count.sessions`). */
function formatAttributedSalesLine(
  subtotal: string | null | undefined,
  currency: string | null | undefined
): string {
  if (subtotal == null) return "—";
  const n = Number.parseFloat(subtotal);
  if (Number.isNaN(n)) return subtotal;
  if (currency && /^[A-Z]{3}$/i.test(currency)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency.toUpperCase(),
      }).format(n);
    } catch {
      /* fall through */
    }
  }
  return `${subtotal}${currency ? ` ${currency}` : ""}`;
}

function campaignPerformanceHint(sessionCount: number): string {
  if (sessionCount === 0) return "No activity yet";
  if (sessionCount < 10) return "Low activity";
  return "Getting engagement";
}

/** List GET returns variants with ids + gameId only — join against catalog from getGames(). */
function pickPrimaryVariant(variants: CampaignVariant[] | undefined): CampaignVariant | undefined {
  const list = variants ?? [];
  return list.find((v) => v.status === "ACTIVE") ?? list[0];
}

function uniqueGamesForCampaign(
  variants: CampaignVariant[] | undefined,
  gamesById: Map<string, Game>
): Game[] {
  const seen = new Set<string>();
  const out: Game[] = [];
  for (const v of variants ?? []) {
    const g = gamesById.get(v.gameId);
    if (g && !seen.has(g.id)) {
      seen.add(g.id);
      out.push(g);
    }
  }
  return out;
}

/**
 * Visual preview only when `game.key` is a known catalog key — same policy as GameCatalogPreview.
 */
function CampaignCardGamePreview({
  game,
  variant,
}: {
  game: Game;
  variant?: CampaignVariant;
}) {
  switch (game.key) {
    case "wheel_of_fortune":
      return (
        <Box maxWidth="96px" minWidth="72px">
          <WheelGamePreview />
        </Box>
      );
    case "scratch_card":
      return (
        <Box maxWidth="96px" minWidth="72px">
          <ScratchGamePreview
            compact
            theme={resolveScratchPreviewTheme(variant?.config)}
          />
        </Box>
      );
    default:
      return (
        <Box
          background="bg-surface-secondary"
          borderRadius="200"
          padding="200"
          minWidth="72px"
          maxWidth="96px"
          minHeight="72px"
        >
          <BlockStack align="center" inlineAlign="center" gap="100">
            <Icon source={AppsIcon} tone="subdued" />
          </BlockStack>
        </Box>
      );
  }
}

function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    activeCampaigns: 0,
    totalCampaigns: 0,
    totalGames: 0,
    totalPolicies: 0,
  });
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  /** Join list-campaign `variant.gameId` to catalog names/keys (GET /admin/campaigns has no nested game). */
  const [gamesCatalog, setGamesCatalog] = useState<Game[]>([]);
  const [rewardPolicies, setRewardPolicies] = useState<RewardPolicy[]>([]);
  const [overview, setOverview] = useState<OverviewMetrics | null>(null);
  const [campaignAttributionSnapshot, setCampaignAttributionSnapshot] = useState<
    CampaignAttributionSnapshotRow[]
  >([]);
  const [playsLast7d, setPlaysLast7d] = useState(0);
  const [campaignStatusActionId, setCampaignStatusActionId] = useState<string | null>(null);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [campaignsData, gamesData, policiesData, overviewData, tsData, attributionRows] =
        await Promise.all([
          getCampaigns(),
          getGames(),
          getRewardPolicies(),
          getAnalyticsOverview().catch(() => null),
          getAnalyticsTimeSeries("7d", "day").catch(() => null),
          getCampaignAttributionSnapshot().catch(() => [] as CampaignAttributionSnapshotRow[]),
        ]);

      setCampaigns(campaignsData);
      setGamesCatalog(gamesData);
      setRewardPolicies(policiesData);
      setOverview(overviewData);
      setCampaignAttributionSnapshot(attributionRows);
      const plays7 =
        tsData?.data?.reduce((sum, p) => sum + p.plays, 0) ?? 0;
      setPlaysLast7d(plays7);

      const activeCampaigns = campaignsData.filter((c) => c.status === "ACTIVE").length;

      setStats({
        activeCampaigns,
        totalCampaigns: campaignsData.length,
        totalGames: gamesData.length,
        totalPolicies: policiesData.length,
      });
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCampaignStatusChange = useCallback(
    async (id: string, newStatus: Campaign["status"]) => {
      setCampaignStatusActionId(id);
      setError(null);
      try {
        await updateCampaign(id, { status: newStatus });
        await loadDashboardData();
      } catch (err) {
        if (isShopInstallRedirectError(err)) {
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to update campaign");
      } finally {
        setCampaignStatusActionId(null);
      }
    },
    [loadDashboardData]
  );

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const isFirstRun = !loading && stats.totalCampaigns === 0;
  const needsSetup = !loading && (stats.totalGames === 0 || stats.totalPolicies === 0);
  const firstActiveCampaign = campaigns.find((c) => c.status === "ACTIVE");

  const opportunities = useMemo(
    () => buildOpportunities(campaigns, stats),
    [campaigns, stats]
  );

  const attentionItems = useMemo(
    () => buildAttentionItems(campaigns, stats),
    [campaigns, stats]
  );

  const rewardsIssued = overview?.rewardsGranted ?? 0;
  const attributedOrdersCount = overview?.attributedOrders ?? 0;
  const attributedSalesMixed = overview?.attributedSalesMixedCurrency ?? false;

  const attributionByCampaignId = useMemo(() => {
    const m = new Map<string, CampaignAttributionSnapshotRow>();
    for (const row of campaignAttributionSnapshot) {
      m.set(row.campaignId, row);
    }
    return m;
  }, [campaignAttributionSnapshot]);

  /** Top 5 campaigns by attributed performance — same data as snapshot + list; no extra API calls. */
  const topCampaignsAttributed = useMemo(() => {
    const byId = new Map(campaigns.map((c) => [c.id, c]));
    const merged: {
      campaign: Campaign;
      attributedOrders: number;
      attributedRevenueSubtotal: string | null;
      attributedSalesMixedCurrency: boolean;
      attributedSalesCurrency: string | null;
    }[] = [];

    for (const row of campaignAttributionSnapshot) {
      const campaign = byId.get(row.campaignId);
      if (!campaign) continue;

      const attributedOrders = row.attributedOrders ?? 0;
      const hasSubtotal = row.attributedRevenueSubtotal != null;
      const hasAttribution = attributedOrders > 0 || hasSubtotal;
      if (!hasAttribution) continue;

      merged.push({
        campaign,
        attributedOrders,
        attributedRevenueSubtotal: row.attributedRevenueSubtotal,
        attributedSalesMixedCurrency: row.attributedSalesMixedCurrency,
        attributedSalesCurrency: row.attributedSalesCurrency,
      });
    }

    merged.sort((a, b) => {
      const aRevenueRanked =
        a.attributedRevenueSubtotal !== null && !a.attributedSalesMixedCurrency;
      const bRevenueRanked =
        b.attributedRevenueSubtotal !== null && !b.attributedSalesMixedCurrency;
      if (aRevenueRanked && !bRevenueRanked) return -1;
      if (!aRevenueRanked && bRevenueRanked) return 1;
      if (aRevenueRanked && bRevenueRanked) {
        const pa = Number.parseFloat(a.attributedRevenueSubtotal!);
        const pb = Number.parseFloat(b.attributedRevenueSubtotal!);
        const na = Number.isNaN(pa) ? 0 : pa;
        const nb = Number.isNaN(pb) ? 0 : pb;
        if (nb !== na) return nb - na;
      }
      return b.attributedOrders - a.attributedOrders;
    });

    const anyMixed = merged.some((m) => m.attributedSalesMixedCurrency);
    return { rows: merged.slice(0, 5), anyMixed };
  }, [campaigns, campaignAttributionSnapshot]);

  const gamesById = useMemo(() => new Map(gamesCatalog.map((g) => [g.id, g])), [gamesCatalog]);
  const policiesById = useMemo(
    () => new Map(rewardPolicies.map((p) => [p.id, p])),
    [rewardPolicies]
  );

  if (loading) {
    return (
      <Page title="Dashboard" fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <BlockStack align="center" gap="400">
                  <Spinner size="large" />
                  <Text as="p" variant="bodyMd" tone="subdued">Loading dashboard...</Text>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const statusBadge = firstActiveCampaign ? (
    <Badge tone="success">Live</Badge>
  ) : (
    <Badge tone="attention">Not live</Badge>
  );

  return (
    <Page
      fullWidth
      title="Dashboard"
      subtitle="Your storefront games and rewards at a glance."
      titleMetadata={statusBadge}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {!isFirstRun && needsSetup && (
          <Layout.Section>
            <Banner tone="warning">
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd">
                  {stats.totalGames === 0 && "No games available. "}
                  {stats.totalPolicies === 0 && "No reward policies configured."}
                </Text>
                <InlineStack gap="200">
                  {stats.totalGames === 0 && (
                    <Button size="slim" onClick={() => navigate("/games")}>View Games</Button>
                  )}
                  {stats.totalPolicies === 0 && (
                    <Button size="slim" onClick={() => navigate("/rewards")}>Create Rewards</Button>
                  )}
                </InlineStack>
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <StorefrontSetupGuidance variant="dashboard" />
        </Layout.Section>

        <Layout.Section>
          <Card
            padding={{ xs: "400", md: "600" }}
            background={firstActiveCampaign ? "bg-surface-success" : "bg-surface-secondary"}
          >
            {firstActiveCampaign ? (
              <InlineGrid columns={{ xs: 1, md: "1fr auto" }} gap="400" alignItems="start">
                <InlineStack gap="400" blockAlign="start" wrap={false}>
                  <Box>
                    <Icon source={CheckCircleIcon} tone="success" />
                  </Box>
                  <BlockStack gap="300">
                    <Text as="p" variant="bodySm" fontWeight="semibold" tone="success">
                      Storefront status
                    </Text>
                    <Text as="p" variant="heading2xl" fontWeight="bold">
                      {firstActiveCampaign.name}
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {firstActiveCampaign.placement}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {playsLast7d} plays in the last 7 days · {stats.activeCampaigns} active of{" "}
                      {stats.totalCampaigns} campaigns
                    </Text>
                  </BlockStack>
                </InlineStack>
                <InlineStack gap="200" wrap>
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/campaigns/${firstActiveCampaign.id}`)}
                  >
                    Open campaign
                  </Button>
                  <Button variant="secondary" onClick={() => navigate("/campaigns")}>
                    All campaigns
                  </Button>
                </InlineStack>
              </InlineGrid>
            ) : (
              <InlineGrid columns={{ xs: 1, md: "1fr auto" }} gap="400" alignItems="start">
                <InlineStack gap="400" blockAlign="start" wrap={false}>
                  <Box>
                    <Icon source={TargetIcon} tone="subdued" />
                  </Box>
                  <BlockStack gap="300">
                    <Text as="p" variant="bodySm" fontWeight="semibold" tone="subdued">
                      Storefront status
                    </Text>
                    <Text as="p" variant="headingLg" fontWeight="bold">
                      Nothing is live for shoppers yet
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {stats.totalCampaigns === 0
                        ? "When you’re ready, add a campaign so games and rewards can appear on your store."
                        : `You have ${stats.totalCampaigns} campaign${stats.totalCampaigns === 1 ? "" : "s"} — activate one to go live.`}
                    </Text>
                  </BlockStack>
                </InlineStack>
                <Box>
                  <Button
                    variant="primary"
                    onClick={() =>
                      navigate(stats.totalCampaigns > 0 ? "/campaigns" : "/campaigns/new")
                    }
                  >
                    {stats.totalCampaigns > 0 ? "View campaigns" : "Create campaign"}
                  </Button>
                </Box>
              </InlineGrid>
            )}
          </Card>
        </Layout.Section>

        <Layout.Section>
          <InlineGrid
            columns={{ xs: 1, md: 2, lg: ["twoThirds", "oneThird"] }}
            gap="500"
            alignItems="start"
          >
            <BlockStack gap="400">
              {isFirstRun && (
                <Card>
                  <Box padding="500">
                    <BlockStack gap="500">
                      <BlockStack gap="200">
                        <Text as="h2" variant="headingLg" fontWeight="bold">
                          Welcome to Gamification Center
                        </Text>
                        <Text as="p" variant="bodyMd" tone="subdued">
                          Engage your customers with interactive games and boost conversions with rewards.
                          Complete the setup steps below to create your first campaign.
                        </Text>
                      </BlockStack>

                      <Divider />

                      <BlockStack gap="400">
                        <Text as="h3" variant="headingMd">Setup Checklist</Text>

                        <Box
                          padding="400"
                          background={stats.totalGames > 0 ? "bg-surface-success" : "bg-surface-secondary"}
                          borderRadius="200"
                        >
                          <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="300" blockAlign="center">
                              <Box>
                                <Icon source={stats.totalGames > 0 ? CheckCircleIcon : AppsIcon} tone={stats.totalGames > 0 ? "success" : "subdued"} />
                              </Box>
                              <BlockStack gap="100">
                                <Text as="p" variant="bodyMd" fontWeight="medium">
                                  1. Browse available games
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                  {stats.totalGames > 0 ? `${stats.totalGames} game available` : "View the games catalog"}
                                </Text>
                              </BlockStack>
                            </InlineStack>
                            <Button
                              variant={stats.totalGames > 0 ? "tertiary" : "secondary"}
                              onClick={() => navigate("/games")}
                              icon={ChevronRightIcon}
                            >
                              {stats.totalGames > 0 ? "View" : "Browse"}
                            </Button>
                          </InlineStack>
                        </Box>

                        <Box
                          padding="400"
                          background={stats.totalPolicies > 0 ? "bg-surface-success" : "bg-surface-secondary"}
                          borderRadius="200"
                        >
                          <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="300" blockAlign="center">
                              <Box>
                                <Icon source={stats.totalPolicies > 0 ? CheckCircleIcon : GiftCardIcon} tone={stats.totalPolicies > 0 ? "success" : "subdued"} />
                              </Box>
                              <BlockStack gap="100">
                                <Text as="p" variant="bodyMd" fontWeight="medium">
                                  2. Configure reward policies
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                  {stats.totalPolicies > 0 ? `${stats.totalPolicies} policy configured` : "Define what customers can win"}
                                </Text>
                              </BlockStack>
                            </InlineStack>
                            <Button
                              variant={stats.totalPolicies > 0 ? "tertiary" : "secondary"}
                              onClick={() => navigate("/rewards")}
                              icon={ChevronRightIcon}
                            >
                              {stats.totalPolicies > 0 ? "View" : "Configure"}
                            </Button>
                          </InlineStack>
                        </Box>

                        <Box
                          padding="400"
                          background="bg-surface-secondary"
                          borderRadius="200"
                        >
                          <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="300" blockAlign="center">
                              <Box>
                                <Icon source={TargetIcon} tone="subdued" />
                              </Box>
                              <BlockStack gap="100">
                                <Text as="p" variant="bodyMd" fontWeight="medium">
                                  3. Create your first campaign
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                  Combine a game with rewards
                                </Text>
                              </BlockStack>
                            </InlineStack>
                            <Button
                              variant="primary"
                              onClick={() => navigate("/campaigns/new")}
                              icon={ChevronRightIcon}
                            >
                              Create
                            </Button>
                          </InlineStack>
                        </Box>
                      </BlockStack>
                    </BlockStack>
                  </Box>
                </Card>
              )}

              <Card>
                <Box padding="400">
                  <BlockStack gap="300">
                    <Text as="h2" variant="headingLg" fontWeight="semibold">
                      Performance snapshot
                    </Text>
                    <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
                      <Box>
                        <BlockStack gap="200">
                          <InlineStack align="space-between" blockAlign="center">
                            <Text as="p" variant="bodySm" tone="subdued">
                              Plays (last 7 days)
                            </Text>
                            <Icon source={PlayIcon} tone="info" />
                          </InlineStack>
                          <Text as="p" variant="heading2xl" fontWeight="bold">
                            {playsLast7d}
                          </Text>
                        </BlockStack>
                      </Box>
                      <Box>
                        <BlockStack gap="200">
                          <InlineStack align="space-between" blockAlign="center">
                            <Text as="p" variant="bodySm" tone="subdued">
                              Active campaigns
                            </Text>
                            <Icon source={TargetIcon} tone="success" />
                          </InlineStack>
                          <Text as="p" variant="heading2xl" fontWeight="bold">
                            {stats.activeCampaigns}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            of {stats.totalCampaigns} total
                          </Text>
                        </BlockStack>
                      </Box>
                      <Box>
                        <BlockStack gap="200">
                          <InlineStack align="space-between" blockAlign="center">
                            <Text as="p" variant="bodySm" tone="subdued">
                              Rewards issued
                            </Text>
                            <Icon source={GiftCardIcon} tone="base" />
                          </InlineStack>
                          <Text as="p" variant="heading2xl" fontWeight="bold">
                            {rewardsIssued}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            All time
                          </Text>
                        </BlockStack>
                      </Box>
                    </InlineGrid>

                    <Divider />

                    <BlockStack gap="300">
                      <Text as="p" variant="headingSm" tone="subdued">
                        Revenue impact
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Checkout orders where our issued discount codes were applied (matched via
                        webhooks). Subtotals are from Shopify order data — not store-wide revenue,
                        profit, or wheel-only codes.
                      </Text>
                      <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
                        <Box>
                          <BlockStack gap="200">
                            <InlineStack align="space-between" blockAlign="center">
                              <Text as="p" variant="bodySm" tone="subdued">
                                Attributed orders
                              </Text>
                              <Icon source={OrderIcon} tone="subdued" />
                            </InlineStack>
                            <Text as="p" variant="heading2xl" fontWeight="bold">
                              {attributedOrdersCount.toLocaleString()}
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              Distinct paid orders with a matched code
                            </Text>
                          </BlockStack>
                        </Box>
                        <Box>
                          <BlockStack gap="200">
                            <InlineStack align="space-between" blockAlign="center">
                              <Text as="p" variant="bodySm" tone="subdued">
                                Attributed sales
                              </Text>
                              <Icon source={ReceiptDollarIcon} tone="subdued" />
                            </InlineStack>
                            {attributedSalesMixed &&
                            overview?.attributedSalesByCurrency &&
                            overview.attributedSalesByCurrency.length > 0 ? (
                              <BlockStack gap="200">
                                <Text as="p" variant="headingLg" fontWeight="semibold">
                                  Mixed currencies
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                  See each campaign’s Results for per-currency subtotal sums — we
                                  don’t combine different currencies into one number here.
                                </Text>
                              </BlockStack>
                            ) : (
                              <>
                                <Text as="p" variant="heading2xl" fontWeight="bold">
                                  {formatAttributedSalesLine(
                                    overview?.attributedRevenueSubtotal ?? null,
                                    overview?.attributedSalesCurrency ?? null
                                  )}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                  Sum of order subtotals (same currency across matched orders)
                                </Text>
                              </>
                            )}
                          </BlockStack>
                        </Box>
                      </InlineGrid>
                    </BlockStack>

                    {topCampaignsAttributed.rows.length > 0 && (
                      <>
                        <Divider />
                        <BlockStack gap="300">
                          <BlockStack gap="100">
                            <Text as="h3" variant="headingSm" fontWeight="semibold">
                              Top campaigns
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              {topCampaignsAttributed.anyMixed
                                ? "Ranked by attributed performance (orders used where sales can't be compared)"
                                : "Ranked by attributed sales"}
                            </Text>
                          </BlockStack>
                          <BlockStack gap="300">
                            {topCampaignsAttributed.rows.map((row) => {
                              const { campaign, attributedOrders, attributedRevenueSubtotal } = row;
                              const showRevenuePrimary =
                                attributedRevenueSubtotal !== null && !row.attributedSalesMixedCurrency;
                              const orderLine = `${attributedOrders.toLocaleString()} ${
                                attributedOrders === 1 ? "order" : "orders"
                              }`;

                              return (
                                <BlockStack key={campaign.id} gap="100">
                                  <Link
                                    to={`/campaigns/${campaign.id}/analytics`}
                                    style={{
                                      textDecoration: "none",
                                      color: "inherit",
                                    }}
                                  >
                                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                                      {campaign.name}
                                    </Text>
                                  </Link>
                                  {showRevenuePrimary ? (
                                    <>
                                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                                        {formatAttributedSalesLine(
                                          attributedRevenueSubtotal,
                                          row.attributedSalesCurrency
                                        )}
                                      </Text>
                                      <Text as="p" variant="bodySm" tone="subdued">
                                        {orderLine}
                                      </Text>
                                    </>
                                  ) : (
                                    <>
                                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                                        {orderLine}
                                      </Text>
                                      {row.attributedSalesMixedCurrency ? (
                                        <Text as="p" variant="bodySm" tone="subdued">
                                          Mixed currencies
                                        </Text>
                                      ) : null}
                                    </>
                                  )}
                                </BlockStack>
                              );
                            })}
                          </BlockStack>
                        </BlockStack>
                      </>
                    )}
                  </BlockStack>
                </Box>
              </Card>

              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingLg" fontWeight="semibold">
                    Your campaigns
                  </Text>
                  {campaigns.length > 6 && (
                    <Button variant="plain" onClick={() => navigate("/campaigns")}>
                      View all campaigns
                    </Button>
                  )}
                </InlineStack>
                <InlineGrid columns={{ xs: 1, sm: 2, lg: 3 }} gap="400">
                  {campaigns.length === 0
                    ? Array.from({ length: 3 }, (_, i) => (
                        <Link
                          key={`campaign-placeholder-${i}`}
                          to="/games"
                          style={{
                            textDecoration: "none",
                            color: "inherit",
                            display: "block",
                          }}
                        >
                          <Box
                            padding="400"
                            background="bg-surface-secondary"
                            borderRadius="200"
                            borderWidth="025"
                            borderColor="border"
                            borderStyle="dashed"
                            minHeight="220px"
                          >
                            <BlockStack align="center" inlineAlign="center" gap="300">
                              <Icon source={PlusIcon} tone="subdued" />
                              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                                Your campaign will appear here
                              </Text>
                            </BlockStack>
                          </Box>
                        </Link>
                      ))
                    : campaigns.slice(0, 6).map((campaign) => {
                      const variants = campaign.variants ?? [];
                      const variantCount = variants.length;
                      const activeVariants = variants.filter((v) => v.status === "ACTIVE").length;
                      const sessions = campaign._count?.sessions ?? 0;
                      const statusBusy = campaignStatusActionId === campaign.id;
                      const primaryVariant = pickPrimaryVariant(variants);
                      const uniqueGames = uniqueGamesForCampaign(variants, gamesById);
                      const primaryGame = primaryVariant ? gamesById.get(primaryVariant.gameId) : undefined;
                      const primaryPolicyName = primaryVariant
                        ? policiesById.get(primaryVariant.rewardPolicyId)?.name
                        : undefined;
                      const attributionSnap = attributionByCampaignId.get(campaign.id);

                      return (
                        <Card key={campaign.id} padding="400">
                          <BlockStack gap="300">
                            <InlineGrid columns={{ xs: "96px 1fr" }} gap="300" alignItems="start">
                              <Box>
                                {primaryGame ? (
                                  <CampaignCardGamePreview game={primaryGame} variant={primaryVariant} />
                                ) : (
                                  <Box
                                    background="bg-surface-secondary"
                                    borderRadius="200"
                                    padding="300"
                                    minHeight="72px"
                                    minWidth="72px"
                                  >
                                    <BlockStack align="center" inlineAlign="center" gap="100">
                                      <Icon source={AppsIcon} tone="subdued" />
                                      <Text as="span" variant="bodyXs" tone="subdued" alignment="center">
                                        No game
                                      </Text>
                                    </BlockStack>
                                  </Box>
                                )}
                              </Box>
                              <BlockStack gap="200">
                                {primaryGame ? (
                                  <BlockStack gap="100">
                                    <Text as="p" variant="bodySm" fontWeight="semibold">
                                      {primaryGame.name}
                                    </Text>
                                    {primaryPolicyName && (
                                      <Text as="p" variant="bodyXs" tone="subdued">
                                        Rewards: {primaryPolicyName}
                                      </Text>
                                    )}
                                    {uniqueGames.length > 1 && (
                                      <Text as="p" variant="bodyXs" tone="subdued">
                                        {uniqueGames.length} game types in this campaign
                                      </Text>
                                    )}
                                  </BlockStack>
                                ) : (
                                  <Text as="p" variant="bodySm" tone="subdued">
                                    No game linked yet
                                  </Text>
                                )}
                                <InlineStack align="space-between" blockAlign="start" gap="200" wrap={false}>
                                  <Box minWidth="0">
                                    <Text as="h3" variant="headingMd" fontWeight="semibold" breakWord>
                                      {campaign.name}
                                    </Text>
                                  </Box>
                                  {campaignStatusBadge(campaign.status)}
                                </InlineStack>
                              </BlockStack>
                            </InlineGrid>
                            <BlockStack gap="100">
                              <Badge tone="info">{formatPlacement(campaign.placement)}</Badge>
                              <Text as="p" variant="bodySm" tone="subdued">
                                {formatDisplayTriggerSummary(campaign)}
                              </Text>
                              <Text as="p" variant="bodySm" tone="subdued">
                                {variantCount} variant{variantCount !== 1 ? "s" : ""} · {activeVariants}{" "}
                                active
                              </Text>
                            </BlockStack>
                            <BlockStack gap="100">
                              <Text as="p" variant="bodySm">
                                Plays: {sessions}
                              </Text>
                              <Text as="p" variant="bodySm" tone="subdued">
                                {campaignPerformanceHint(sessions)}
                              </Text>
                            </BlockStack>
                            {attributionSnap && (
                              <BlockStack gap="100">
                                <Text as="p" variant="bodySm">
                                  <Text as="span" tone="subdued">
                                    Attributed orders ·{" "}
                                  </Text>
                                  {attributionSnap.attributedOrders.toLocaleString()}
                                </Text>
                                {attributionSnap.attributedSalesMixedCurrency ? (
                                  <Text as="p" variant="bodySm" tone="subdued">
                                    Attributed sales · Mixed currencies — open Results for
                                    per-currency subtotals (issued codes only).
                                  </Text>
                                ) : (
                                  <Text as="p" variant="bodySm">
                                    <Text as="span" tone="subdued">
                                      Attributed sales ·{" "}
                                    </Text>
                                    {formatAttributedSalesLine(
                                      attributionSnap.attributedRevenueSubtotal,
                                      attributionSnap.attributedSalesCurrency
                                    )}
                                  </Text>
                                )}
                                <Text as="p" variant="bodyXs" tone="subdued">
                                  From checkout orders matched to codes we issued for this
                                  campaign — order subtotals, not profit or full-store revenue.
                                </Text>
                              </BlockStack>
                            )}
                            <ButtonGroup>
                              <Button
                                variant="primary"
                                size="slim"
                                disabled={statusBusy}
                                onClick={() => navigate(`/campaigns/${campaign.id}`)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="secondary"
                                size="slim"
                                disabled={statusBusy}
                                onClick={() => navigate(`/campaigns/${campaign.id}/analytics`)}
                              >
                                Results
                              </Button>
                              {campaign.status === "ACTIVE" && (
                                <Button
                                  size="slim"
                                  disabled={statusBusy}
                                  onClick={() => handleCampaignStatusChange(campaign.id, "PAUSED")}
                                >
                                  Pause
                                </Button>
                              )}
                              {campaign.status === "PAUSED" && (
                                <Button
                                  size="slim"
                                  disabled={statusBusy}
                                  onClick={() => handleCampaignStatusChange(campaign.id, "ACTIVE")}
                                >
                                  Activate
                                </Button>
                              )}
                            </ButtonGroup>
                          </BlockStack>
                        </Card>
                      );
                    })}
                </InlineGrid>
              </BlockStack>
            </BlockStack>

            <BlockStack gap="300">
              {attentionItems.length > 0 && (
                <Card>
                  <Box padding="300">
                    <BlockStack gap="200">
                      <InlineStack gap="200" blockAlign="center">
                        <Icon source={AlertCircleIcon} tone="warning" />
                        <Text as="h2" variant="headingMd" fontWeight="semibold">
                          Needs attention
                        </Text>
                      </InlineStack>
                      <BlockStack gap="0">
                        {attentionItems.map((item, index) => (
                          <BlockStack key={item.id} gap="300">
                            {index > 0 && <Divider />}
                            <InlineStack
                              align="space-between"
                              blockAlign="start"
                              gap="300"
                              wrap
                            >
                              <BlockStack gap="100">
                                <Text as="p" variant="bodyMd" fontWeight="semibold">
                                  {item.label}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                  {item.detail}
                                </Text>
                              </BlockStack>
                              <Box minWidth="fit-content">
                                <Button size="slim" onClick={() => navigate(item.path)}>{item.cta}</Button>
                              </Box>
                            </InlineStack>
                          </BlockStack>
                        ))}
                      </BlockStack>
                    </BlockStack>
                  </Box>
                </Card>
              )}

              {opportunities.length > 0 && (
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd" fontWeight="semibold">
                    Grow your store
                  </Text>
                  <BlockStack gap="200">
                    {opportunities.map((o) => (
                      <Card key={o.id}>
                        <Box padding="300">
                          <BlockStack gap="200">
                            <Text as="h3" variant="headingMd">
                              {o.title}
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              {o.body}
                            </Text>
                            <Box>
                              <Button size="slim" onClick={() => navigate(o.path)}>{o.cta}</Button>
                            </Box>
                          </BlockStack>
                        </Box>
                      </Card>
                    ))}
                  </BlockStack>
                </BlockStack>
              )}

              <Card>
                <Box padding="300">
                  <BlockStack gap="200">
                    <Text as="h2" variant="headingMd" fontWeight="semibold">
                      Quick links
                    </Text>
                    <BlockStack gap="100">
                      <Button variant="plain" textAlign="left" onClick={() => navigate("/games")}>
                        Games catalog
                      </Button>
                      <Button variant="plain" textAlign="left" onClick={() => navigate("/rewards")}>
                        Reward policies
                      </Button>
                      <Button variant="plain" textAlign="left" onClick={() => navigate("/settings")}>
                        Settings
                      </Button>
                    </BlockStack>
                  </BlockStack>
                </Box>
              </Card>
            </BlockStack>
          </InlineGrid>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default DashboardPage;
